import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiMessageSquare, FiClock, FiCheckCircle, FiAlertCircle, FiX, FiLifeBuoy, FiFilter } from 'react-icons/fi';
import { API_URL } from '../../api/client';

interface SupportTicket {
  ticket_id: string;
  ticket_number: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  message_count: number;
  unread_staff_replies: number;
  created_at: string;
  updated_at: string;
}

const Support: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');

  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: '',
    priority: 'normal',
  });

  const categories = [
    'Getting Started',
    'Loan Process',
    'Repayment',
    'Account',
    'Technical',
    'Other',
  ];

  useEffect(() => {
    fetchTickets();
  }, [statusFilter]);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(
        `${API_URL}/customer/support/tickets?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();
      if (result.success) {
        setTickets(result.data);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/customer/support/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      if (result.success) {
        alert('Support ticket created successfully!');
        setShowCreateModal(false);
        resetForm();
        fetchTickets();
      } else {
        alert(result.message || 'Failed to create ticket');
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('An error occurred while creating the ticket');
    }
  };

  const resetForm = () => {
    setFormData({
      subject: '',
      description: '',
      category: '',
      priority: 'normal',
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <FiAlertCircle className="text-green-600" size={20} />;
      case 'in_progress':
        return <FiClock className="text-amber-600" size={20} />;
      case 'closed':
        return <FiCheckCircle className="text-gray-500" size={20} />;
      default:
        return <FiMessageSquare size={20} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-green-50 text-green-700 border border-green-200';
      case 'in_progress':
        return 'bg-amber-50 text-amber-700 border border-amber-200';
      case 'closed':
        return 'bg-gray-50 text-gray-700 border border-gray-200';
      default:
        return 'bg-gray-50 text-gray-700 border border-gray-200';
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-700 border border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-700 border border-orange-200';
      case 'normal':
        return 'bg-blue-100 text-blue-700 border border-blue-200';
      case 'low':
        return 'bg-gray-100 text-gray-700 border border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Calculate stats
  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    closed: tickets.filter(t => t.status === 'closed').length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Elegant Header */}
        <div className="mb-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/40 flex-shrink-0">
                <FiLifeBuoy className="text-white" size={32} />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">Support Center</h1>
                <p className="text-lg text-gray-600">We're here to help you with any questions or issues</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-7 py-4 bg-gradient-to-r from-blue-600 via-blue-600 to-indigo-600 text-white font-semibold rounded-2xl hover:from-blue-700 hover:via-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-500/50 hover:-translate-y-0.5 flex-shrink-0"
            >
              <FiPlus size={22} strokeWidth={2.5} /> New Ticket
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Total Tickets</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                <FiMessageSquare className="text-gray-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-green-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 mb-1">Open</p>
                <p className="text-3xl font-bold text-green-700">{stats.open}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <FiAlertCircle className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-amber-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600 mb-1">In Progress</p>
                <p className="text-3xl font-bold text-amber-700">{stats.inProgress}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <FiClock className="text-amber-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Closed</p>
                <p className="text-3xl font-bold text-gray-700">{stats.closed}</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                <FiCheckCircle className="text-gray-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Elegant Filter Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <FiFilter className="text-gray-600" size={20} />
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Filter by Status</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setStatusFilter('')}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                !statusFilter
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              All Tickets
            </button>
            <button
              onClick={() => setStatusFilter('open')}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                statusFilter === 'open'
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-500/30'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              Open
            </button>
            <button
              onClick={() => setStatusFilter('in_progress')}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                statusFilter === 'in_progress'
                  ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg shadow-amber-500/30'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              In Progress
            </button>
            <button
              onClick={() => setStatusFilter('closed')}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                statusFilter === 'closed'
                  ? 'bg-gradient-to-r from-gray-600 to-slate-600 text-white shadow-lg shadow-gray-500/30'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              Closed
            </button>
          </div>
        </div>

        {/* Elegant Tickets List */}
        {tickets.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-20 text-center">
            <div className="max-w-md mx-auto">
              <div className="relative w-24 h-24 mx-auto mb-8">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-3xl transform rotate-6"></div>
                <div className="relative w-full h-full bg-gradient-to-br from-blue-500 to-indigo-500 rounded-3xl flex items-center justify-center shadow-lg shadow-blue-500/40">
                  <FiMessageSquare className="text-white" size={48} strokeWidth={1.5} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No tickets found</h3>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                {statusFilter
                  ? `You don't have any ${statusFilter.replace('_', ' ')} tickets at the moment.`
                  : "You haven't created any support tickets yet. Start a conversation with our team!"
                }
              </p>
              {!statusFilter && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-500/50 hover:-translate-y-0.5"
                >
                  <FiPlus size={20} /> Create Your First Ticket
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {tickets.map((ticket) => (
              <div
                key={ticket.ticket_id}
                onClick={() => navigate(`/customer/support/${ticket.ticket_id}`)}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7 hover:shadow-xl hover:border-blue-200 cursor-pointer transition-all duration-300 group hover:-translate-y-1"
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                        {getStatusIcon(ticket.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-xl font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                            {ticket.subject}
                          </h3>
                          {ticket.unread_staff_replies > 0 && (
                            <span className="flex-shrink-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-lg shadow-red-500/30 animate-pulse">
                              {ticket.unread_staff_replies} new
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="inline-flex items-center text-gray-500 font-semibold">
                            #{ticket.ticket_number}
                          </span>
                          <span className="text-gray-300">•</span>
                          <span className="text-gray-600 font-medium">{ticket.category}</span>
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-700 text-sm mb-5 line-clamp-2 leading-relaxed pl-15">{ticket.description}</p>

                    <div className="flex flex-wrap items-center gap-3 pl-15">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wide ${getPriorityBadge(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                      <div className="inline-flex items-center gap-2 text-gray-600 bg-gray-50 px-3 py-1.5 rounded-xl">
                        <FiMessageSquare size={15} />
                        <span className="text-sm font-semibold">{ticket.message_count}</span>
                      </div>
                      <span className="text-sm text-gray-500 font-medium">
                        {new Date(ticket.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    <span className={`inline-flex items-center px-4 py-2 text-xs font-bold rounded-xl uppercase tracking-wide ${getStatusColor(ticket.status)}`}>
                      {ticket.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Elegant Create Ticket Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl transform transition-all">
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 px-8 py-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                      <FiLifeBuoy className="text-white" size={24} />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Create Support Ticket</h2>
                      <p className="text-blue-100 text-sm mt-0.5">We're here to assist you</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="p-2.5 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-all duration-200"
                  >
                    <FiX size={24} />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-base font-medium"
                      required
                    >
                      <option value="">Select a category</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-base"
                      placeholder="Brief description of your issue"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none text-base"
                      rows={6}
                      placeholder="Please provide detailed information about your issue..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-base font-medium"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-4 mt-10 pt-8 border-t-2 border-gray-100">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-6 py-4 border-2 border-gray-300 rounded-2xl text-gray-700 font-bold hover:bg-gray-50 transition-all text-base"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-500/50 text-base"
                  >
                    Create Ticket
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <style>{`
        /* Mobile touch target enhancements */
        @media (max-width: 768px) {
          button {
            min-height: 44px;
            touch-action: manipulation;
          }

          .text-4xl {
            font-size: 1.875rem !important;
          }

          .text-3xl {
            font-size: 1.5rem !important;
          }

          .text-2xl {
            font-size: 1.25rem !important;
          }

          .text-xl {
            font-size: 1.0625rem !important;
          }

          .px-7 {
            padding-left: 1.25rem !important;
            padding-right: 1.25rem !important;
          }

          .px-8 {
            padding-left: 1.25rem !important;
            padding-right: 1.25rem !important;
          }

          .py-8 {
            padding-top: 1.5rem !important;
            padding-bottom: 1.5rem !important;
          }

          .gap-6 {
            gap: 1rem !important;
          }

          .p-20 {
            padding: 2rem !important;
          }

          .p-7 {
            padding: 1.25rem !important;
          }

          .max-w-2xl {
            max-width: 100% !important;
          }
        }

        @media (max-width: 480px) {
          .text-4xl {
            font-size: 1.625rem !important;
          }

          .text-2xl {
            font-size: 1.125rem !important;
          }

          .text-xl {
            font-size: 1rem !important;
          }

          .px-8,
          .px-7 {
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }

          .p-20 {
            padding: 1.5rem !important;
          }

          .w-16.h-16 {
            width: 3rem !important;
            height: 3rem !important;
          }

          .w-12.h-12 {
            width: 2.5rem !important;
            height: 2.5rem !important;
          }
        }
      `}</style>
    </div>
  );
};

  export default Support;
